import { z } from 'zod';

import {
  auditFieldsSchema,
  entityIdSchema,
  isoDateSchema,
  isoDateTimeSchema,
  moneySchema,
  pageMetaSchema,
} from './common.js';
import { customerInputSchema, riskStatusSchema } from './customers.js';
import {
  appliedPromoCodeSchema,
  commercialDiscountSchema,
  priceLineInputSchema,
  priceLineSchema,
} from './pricing.js';

export const reservationStatusSchema = z.enum([
  'draft',
  'option',
  'confirmed',
  'waitlisted',
  'cancelled',
  'completed',
  'no_show',
]);

export const reservationInitialStatusSchema = z.enum([
  'draft',
  'option',
  'confirmed',
  'waitlisted',
]);

export const reservationAgeBandSchema = z.enum(['under_5', 'under_12', 'adult']);
export const reservationDietKindSchema = z.enum([
  'none',
  'vegetarian',
  'vegan',
  'medical',
  'religious',
  'other',
]);

export const reservationDietSchema = z.object({
  kind: reservationDietKindSchema.default('none'),
  details: z.string().trim().max(1_000).default(''),
});

export const reservationParticipantInputSchema = z.object({
  id: entityIdSchema.optional(),
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().min(1).max(120),
  birthDate: isoDateSchema.optional(),
  allergies: z.array(z.string().trim().min(1).max(160)).max(30).default([]),
  diet: reservationDietSchema.default({ kind: 'none', details: '' }),
  notes: z.string().trim().max(2_000).default(''),
});

// Alias conservé pour les consommateurs du socle Séjours/Planning.
export const participantInputSchema = reservationParticipantInputSchema;

export const reservationParticipantSchema = reservationParticipantInputSchema
  .omit({ id: true })
  .extend({
    id: entityIdSchema,
    reservationId: entityIdSchema,
    ageBand: reservationAgeBandSchema,
    isChild: z.boolean(),
    hasChildDocument: z.boolean(),
    revision: z.number().int().positive(),
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
  });

export const reservationCustomerSelectionSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('existing'),
    customerId: entityIdSchema,
  }),
  z.object({
    mode: z.literal('new'),
    profile: customerInputSchema,
    duplicateAcknowledgement: z.boolean().default(false),
  }),
]);

const canonicalReservationCreationSchema = z.object({
  customer: reservationCustomerSelectionSchema,
  stayId: entityIdSchema,
  status: reservationInitialStatusSchema.default('draft'),
  participants: z.array(reservationParticipantInputSchema).min(1).max(100),
  totalCents: moneySchema,
  pricingLines: z.array(priceLineInputSchema).max(200).default([]),
  discount: commercialDiscountSchema.default({ type: 'none', value: 0, label: '' }),
  note: z.string().trim().max(10_000).default(''),
  redListAcknowledgement: z.boolean().default(false),
});

const legacyReservationCreationSchema = z
  .object({
    customerId: entityIdSchema,
    stayId: entityIdSchema,
    status: reservationInitialStatusSchema.default('draft'),
    participants: z.array(reservationParticipantInputSchema).min(1).max(100),
    totalCents: moneySchema,
    pricingLines: z.array(priceLineInputSchema).max(200).default([]),
    discount: commercialDiscountSchema.default({ type: 'none', value: 0, label: '' }),
    notes: z.string().trim().max(10_000).default(''),
    redListAcknowledgement: z.boolean().default(false),
  })
  .transform((input) => ({
    customer: { mode: 'existing' as const, customerId: input.customerId },
    stayId: input.stayId,
    status: input.status,
    participants: input.participants,
    totalCents: input.totalCents,
    pricingLines: input.pricingLines,
    discount: input.discount,
    note: input.notes,
    redListAcknowledgement: input.redListAcknowledgement,
  }));

/**
 * Le premier membre est le contrat v63 canonique. Le second ne fait que normaliser
 * les requêtes déjà émises par le socle 5.1.1 vers cette union discriminée.
 */
export const reservationCreationInputSchema = z.union([
  canonicalReservationCreationSchema,
  legacyReservationCreationSchema,
]);

// Alias temporaire du contrat d'entrée historique ; sa sortie est toujours canonique.
export const reservationInputSchema = reservationCreationInputSchema;

export const reservationUpdateInputSchema = z.object({
  revision: z.number().int().positive(),
  customerId: entityIdSchema.optional(),
  stayId: entityIdSchema.optional(),
  participants: z.array(reservationParticipantInputSchema).min(1).max(100).optional(),
  totalCents: moneySchema.optional(),
  pricingLines: z.array(priceLineInputSchema).min(1).max(200).optional(),
  discount: commercialDiscountSchema.optional(),
  note: z.string().trim().max(10_000).optional(),
  redListAcknowledgement: z.boolean().default(false),
});

export const reservationUpdateSchema = reservationUpdateInputSchema;

export const reservationTransitionInputSchema = z.object({
  revision: z.number().int().positive(),
  status: reservationStatusSchema,
  reason: z.string().trim().max(2_000).default(''),
});

export const reservationTransitionSchema = reservationTransitionInputSchema;

export const reservationCancellationInputSchema = z.object({
  revision: z.number().int().positive(),
  reason: z.string().trim().min(1).max(2_000),
});

export const reservationValidationInputSchema = z.object({
  revision: z.number().int().positive(),
});

export const reservationNoteInputSchema = z.object({
  revision: z.number().int().positive(),
  body: z.string().trim().max(10_000),
});

export const reservationListSortSchema = z.enum([
  'starts_on_desc',
  'starts_on_asc',
  'created_at_desc',
  'created_at_asc',
]);

export const reservationTileFilterSchema = z.enum([
  'to_validate',
  'payment_pending',
  'payment_default',
  'waitlisted',
  'expired_options',
  'cancelled',
]);

export const reservationListQuerySchema = z.object({
  cursor: z.string().trim().min(1).max(1_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).optional(),
  status: reservationStatusSchema.optional(),
  type: z.string().trim().max(160).optional(),
  startsFrom: isoDateSchema.optional(),
  startsTo: isoDateSchema.optional(),
  initial: z
    .string()
    .trim()
    .regex(/^\p{L}$/u)
    .optional(),
  sort: reservationListSortSchema.default('starts_on_desc'),
  tile: reservationTileFilterSchema.optional(),
});

export const reservationListCursorSchema = z.object({
  sort: reservationListSortSchema,
  value: z.string().min(1).max(100),
  id: entityIdSchema,
});

const reservationFinanceSummaryShape = {
  totalCents: moneySchema,
  cashPaidCents: moneySchema,
  creditAppliedCents: moneySchema,
  giftVoucherAppliedCents: moneySchema,
  settledCents: moneySchema,
  /** Alias de compatibilité : il représente uniquement le cash encaissé. */
  paidCents: moneySchema,
  balanceDueCents: z.number().int(),
} as const;

function normalizeLegacyReservationFinance(value: unknown): unknown {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return value;
  const finance = value as Readonly<Record<string, unknown>>;
  if (typeof finance.paidCents !== 'number') return value;
  const cashPaidCents =
    typeof finance.cashPaidCents === 'number' ? finance.cashPaidCents : finance.paidCents;
  const creditAppliedCents =
    typeof finance.creditAppliedCents === 'number' ? finance.creditAppliedCents : 0;
  const giftVoucherAppliedCents =
    typeof finance.giftVoucherAppliedCents === 'number' ? finance.giftVoucherAppliedCents : 0;
  return {
    ...finance,
    grossCashPaidCents:
      typeof finance.grossCashPaidCents === 'number' ? finance.grossCashPaidCents : cashPaidCents,
    cashRefundedCents:
      typeof finance.cashRefundedCents === 'number' ? finance.cashRefundedCents : 0,
    creditIssuedCents:
      typeof finance.creditIssuedCents === 'number' ? finance.creditIssuedCents : 0,
    refundableCashCents:
      typeof finance.refundableCashCents === 'number' ? finance.refundableCashCents : cashPaidCents,
    cashPaidCents,
    creditAppliedCents,
    giftVoucherAppliedCents,
    settledCents:
      finance.settledCents ?? cashPaidCents + creditAppliedCents + giftVoucherAppliedCents,
  };
}

export const reservationFinanceSummarySchema = z.preprocess(
  normalizeLegacyReservationFinance,
  z.object(reservationFinanceSummaryShape),
);

const reservationListItemBaseSchema = z.object({
  id: entityIdSchema,
  reference: z.string().min(1),
  customerId: entityIdSchema,
  customerName: z.string().min(1),
  stayId: entityIdSchema,
  stayReference: z.string().min(1),
  stayName: z.string().min(1),
  stayType: z.string().min(1),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  participantCount: z.number().int().positive(),
  status: reservationStatusSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  revision: z.number().int().positive(),
});

/**
 * Les deux projections sont explicites : la forme `omitted` ne transporte aucun
 * montant, tandis que la forme `granted` porte le sous-objet Finance complet.
 */
export const reservationListItemSchema = z.discriminatedUnion('financeVisibility', [
  reservationListItemBaseSchema
    .extend({
      financeVisibility: z.literal('granted'),
      finance: reservationFinanceSummarySchema,
    })
    .strict(),
  reservationListItemBaseSchema
    .extend({
      financeVisibility: z.literal('omitted'),
    })
    .strict(),
]);

export const reservationPageSchema = z.object({
  items: z.array(reservationListItemSchema),
  page: pageMetaSchema,
});

export const reservationTilesSchema = z.object({
  toValidate: z.number().int().nonnegative(),
  paymentPending: z.number().int().nonnegative(),
  paymentDefault: z.number().int().nonnegative(),
  waitlisted: z.number().int().nonnegative(),
  expiredOptions: z.number().int().nonnegative(),
  cancelled: z.number().int().nonnegative(),
});

export const reservationCustomerSummarySchema = z.object({
  id: entityIdSchema,
  reference: z.string().min(1),
  name: z.string().min(1),
  riskStatus: riskStatusSchema,
  riskReason: z.string(),
});

export const reservationStaySummarySchema = z.object({
  id: entityIdSchema,
  reference: z.string().min(1),
  name: z.string().min(1),
  type: z.string().min(1),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
});

export const reservationPaymentReadSchema = z.object({
  id: entityIdSchema,
  amountCents: z.number().int(),
  method: z.string().min(1),
  status: z.string().min(1),
  paidOn: isoDateSchema.nullable(),
  reference: z.string(),
  createdAt: isoDateTimeSchema,
});

export const reservationFinanceSchema = z.preprocess(
  normalizeLegacyReservationFinance,
  z.object({
    ...reservationFinanceSummaryShape,
    grossCashPaidCents: moneySchema,
    cashRefundedCents: moneySchema,
    creditIssuedCents: moneySchema,
    refundableCashCents: moneySchema,
    payments: z.array(reservationPaymentReadSchema),
    pricingLines: z.array(priceLineSchema),
    discount: commercialDiscountSchema,
    appliedPromoCodes: z.array(appliedPromoCodeSchema).default([]),
    sourceQuoteId: entityIdSchema.nullable(),
    sourceOptionId: entityIdSchema.nullable(),
  }),
);

export const reservationNoteSchema = z.object({
  id: entityIdSchema,
  reservationId: entityIdSchema,
  body: z.string(),
  revision: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  createdBy: entityIdSchema.nullable(),
  updatedAt: isoDateTimeSchema,
  updatedBy: entityIdSchema.nullable(),
  archivedAt: isoDateTimeSchema.nullable(),
});

export const reservationHistoryEventSchema = z.object({
  id: entityIdSchema,
  kind: z.enum([
    'created',
    'status',
    'updated',
    'participant',
    'note',
    'manual_validation',
    'cancellation',
    'payment',
    'audit',
  ]),
  action: z.string().min(1),
  detail: z.string(),
  occurredAt: isoDateTimeSchema,
  actorId: entityIdSchema.nullable(),
});

export const reservationHistorySchema = z.object({
  items: z.array(reservationHistoryEventSchema),
});

/** Synthèse légère : aucune collection d'onglet et aucun montant. */
export const reservationDetailSchema = z
  .object({
    id: entityIdSchema,
    reference: z.string().min(1),
    status: reservationStatusSchema,
    customer: reservationCustomerSummarySchema,
    stay: reservationStaySummarySchema,
    participantCount: z.number().int().positive(),
    validatedWithoutPayment: z.boolean(),
    validatedWithoutPaymentAt: isoDateTimeSchema.nullable(),
    validatedWithoutPaymentBy: entityIdSchema.nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    cancelledBy: entityIdSchema.nullable(),
    cancellationReason: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
    revision: z.number().int().positive(),
  })
  .strict();

export const reservationParticipantsSchema = z
  .object({
    items: z.array(reservationParticipantSchema).min(1),
  })
  .strict();

export const reservationNotesSchema = z
  .object({
    items: z.array(reservationNoteSchema),
    customerNote: z.string(),
  })
  .strict();

export const reservationSummarySchema = reservationDetailSchema;

// Contrat de compatibilité interne des services antérieurs au read-model hydraté.
export const reservationSchema = z
  .object({
    id: entityIdSchema,
    customerId: entityIdSchema,
    stayId: entityIdSchema,
    status: reservationStatusSchema,
    participants: z
      .array(reservationParticipantInputSchema.extend({ diet: reservationDietSchema.optional() }))
      .min(1),
    totalCents: moneySchema,
    notes: z.string(),
    cancelledAt: isoDateTimeSchema.nullable(),
    cancellationReason: z.string().nullable(),
  })
  .and(auditFieldsSchema);

// Contrat financier historique conservé pour le futur domaine Avoirs.
export const cancellationInputSchema = z.object({
  reason: z.string().trim().max(2_000).optional(),
  issueCredit: z.boolean().default(false),
  retainedCents: moneySchema.default(0),
  idempotencyKey: z.string().trim().min(8).max(200),
});

export const optionModeSchema = z.enum(['manual', 'automatic']);

export const optionInputSchema = z
  .object({
    quoteId: entityIdSchema.nullable().default(null),
    stayId: entityIdSchema,
    customerId: entityIdSchema,
    mode: optionModeSchema.default('manual'),
    expiresAt: isoDateTimeSchema.optional(),
    policyDurationMinutes: z.number().int().positive().max(525_600).nullable().default(null),
    participantCount: z.number().int().positive().max(100),
    idempotencyKey: z.string().trim().min(8).max(200).optional(),
  })
  .superRefine((input, context) => {
    if (input.mode === 'manual' && input.expiresAt === undefined) {
      context.addIssue({
        code: 'custom',
        path: ['expiresAt'],
        message: "L'expiration manuelle est obligatoire.",
      });
    }
    if (input.mode === 'automatic' && input.policyDurationMinutes === null) {
      context.addIssue({
        code: 'custom',
        path: ['policyDurationMinutes'],
        message: 'La durée automatique est obligatoire.',
      });
    }
  });

export const optionSchema = z.object({
  id: entityIdSchema,
  quoteId: entityIdSchema.nullable(),
  stayId: entityIdSchema,
  customerId: entityIdSchema,
  reservationId: entityIdSchema.nullable(),
  mode: optionModeSchema,
  expiresAt: isoDateTimeSchema,
  policyDurationMinutes: z.number().int().positive().nullable(),
  state: z.enum(['active', 'expired', 'cancelled', 'converted', 'linked']),
  convertedReservationId: entityIdSchema.nullable(),
  cancelledAt: isoDateTimeSchema.nullable(),
  cancellationReason: z.string().nullable(),
  cancelledBy: entityIdSchema.nullable(),
  participantCount: z.number().int().positive(),
  revision: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  updatedBy: entityIdSchema.nullable(),
});

export const optionPageSchema = z.object({
  items: z.array(optionSchema),
  page: pageMetaSchema,
});

export const optionUpdateSchema = z
  .object({
    revision: z.number().int().positive(),
    expiresAt: isoDateTimeSchema.optional(),
    policyDurationMinutes: z.number().int().positive().max(525_600).nullable().optional(),
    idempotencyKey: z.string().trim().min(8).max(200).optional(),
  })
  .superRefine((input, context) => {
    if (input.expiresAt === undefined && input.policyDurationMinutes === undefined) {
      context.addIssue({
        code: 'custom',
        message: 'Une nouvelle expiration ou durée est obligatoire.',
      });
    }
  });

export const optionCancellationSchema = z.object({
  revision: z.number().int().positive(),
  reason: z.string().trim().max(2_000).default(''),
  idempotencyKey: z.string().trim().min(8).max(200).optional(),
});

export type Reservation = z.infer<typeof reservationSchema>;
export type ReservationStatus = z.infer<typeof reservationStatusSchema>;
export type ReservationListQuery = z.infer<typeof reservationListQuerySchema>;
export type ReservationListCursor = z.infer<typeof reservationListCursorSchema>;
export type ReservationListItem = z.infer<typeof reservationListItemSchema>;
export type ReservationTiles = z.infer<typeof reservationTilesSchema>;
export type ReservationDetail = z.infer<typeof reservationDetailSchema>;
export type ReservationSummary = z.infer<typeof reservationSummarySchema>;
export type ReservationParticipants = z.infer<typeof reservationParticipantsSchema>;
export type ReservationNotes = z.infer<typeof reservationNotesSchema>;
export type ReservationParticipant = z.infer<typeof reservationParticipantSchema>;
export type ReservationParticipantInput = z.infer<typeof reservationParticipantInputSchema>;
export type ReservationCreationInput = z.output<typeof reservationCreationInputSchema>;
export type ReservationCustomerSelection = z.infer<typeof reservationCustomerSelectionSchema>;
export type ReservationUpdateInput = z.infer<typeof reservationUpdateInputSchema>;
export type ReservationTransitionInput = z.infer<typeof reservationTransitionInputSchema>;
export type ReservationCancellationInput = z.infer<typeof reservationCancellationInputSchema>;
export type ReservationNote = z.infer<typeof reservationNoteSchema>;
export type ReservationHistoryEvent = z.infer<typeof reservationHistoryEventSchema>;
export type ReservationFinance = z.infer<typeof reservationFinanceSchema>;

// Aliases utilisés par les services 5.1.1, désormais normalisés vers le contrat canonique.
export type ReservationInput = ReservationCreationInput;
export type ReservationUpdate = ReservationUpdateInput;
export type Option = z.infer<typeof optionSchema>;
export type OptionInput = z.infer<typeof optionInputSchema>;
